import { z } from 'zod';

import {
  REPORT_WORKFLOW_STATES,
  MERCHANT_REPORT_RISK_LEVELS,
  MERCHANT_REPORT_STATUSES,
  MERCHANT_REPORT_TYPES,
  RISK_INDICATOR_RISK_LEVELS,
  STAKEHOLDER_ROLES,
} from '../consts';
import { reportSchemaRegistry } from './openapi/registry';

export const RiskLevelEnum = z.enum(['critical', 'warning', 'positive']);

export const SocialPageStatusEnum = z
  .enum(['detected', 'missing'])
  .openapi({ description: 'Status of social media page detection' });

const FacebookPageBase = z.object({
  id: z.string().nullish().openapi({ description: 'Facebook page ID' }),
  name: z.string().nullish().openapi({ description: 'Page name' }),
  email: z.string().nullish().openapi({ description: 'Contact email' }),
  likes: z.number().nullish().openapi({ description: 'Number of page likes' }),
  address: z.string().nullish().openapi({ description: 'Business address' }),
  categories: z.array(z.string()).nullish().openapi({ description: 'Page categories' }),
  phoneNumber: z.string().nullish().openapi({ description: 'Contact phone number' }),
  creationDate: z.string().nullish().openapi({ description: 'Page creation date' }),
  screenshotUrl: z.string().url().nullish().openapi({ description: 'Screenshot URL of the page' }),
});

export const FacebookPageSchema = reportSchemaRegistry.register(
  'FacebookPage',
  z
    .union([
      FacebookPageBase.extend({
        url: z.string(),
        status: z.literal('detected'),
      }),
      FacebookPageBase.extend({
        url: z.null(),
        status: z.literal('missing'),
      }),
    ])
    .openapi({ description: 'Facebook page analysis data' }),
);

const InstagramPageBase = z.object({
  id: z.string().nullish().openapi({ description: 'Instagram page ID' }),
  username: z.string().nullish().openapi({ description: 'Instagram username' }),
  biography: z.string().nullish().openapi({ description: 'Profile biography' }),
  followers: z.number().nullish().openapi({ description: 'Number of followers' }),
  categories: z.array(z.string()).nullish().openapi({ description: 'Profile categories' }),
  isVerified: z.boolean().nullish().openapi({ description: 'Whether the account is verified' }),
  screenshotUrl: z
    .string()
    .url()
    .nullish()
    .openapi({ description: 'Screenshot URL of the profile' }),
  isBusinessProfile: z
    .boolean()
    .nullish()
    .openapi({ description: 'Whether this is a business profile' }),
});

export const InstagramPageSchema = reportSchemaRegistry.register(
  'InstagramPage',
  z
    .union([
      InstagramPageBase.extend({
        url: z.string(),
        status: z.literal('detected'),
      }),
      InstagramPageBase.extend({
        url: z.null(),
        status: z.literal('missing'),
      }),
    ])
    .openapi({ description: 'Instagram page analysis data' }),
);

export const SocialPage = reportSchemaRegistry.register(
  'SocialPage',
  z
    .union([
      z.object({
        url: z.string(),
        status: z.literal('detected'),
      }),
      z.object({
        url: z.null(),
        status: z.literal('missing'),
      }),
    ])
    .openapi({ description: 'Generic social media page data' }),
);

export const MccSuggestionSchema = reportSchemaRegistry.register(
  'MccSuggestion',
  z
    .object({
      mcc: z.string().openapi({ description: 'Merchant Category Code', example: '7399' }),
      description: z.string().nullish().openapi({
        description: 'MCC description',
        example: 'Business Services, Not Elsewhere Classified',
      }),
      reasoning: z
        .string()
        .nullish()
        .openapi({ description: 'Reasoning behind the MCC selection' }),
    })
    .openapi({ description: 'Merchant Category Code suggestion with reasoning' }),
);

export const MerchantCategoryCodeSummarySchema = reportSchemaRegistry.register(
  'MerchantCategoryCodeSummary',
  z
    .object({
      primary: MccSuggestionSchema.nullish().openapi({ description: 'Primary MCC assignment' }),
      alternatives: z
        .array(MccSuggestionSchema)
        .max(2)
        .default([])
        .openapi({ description: 'Alternative MCC suggestions ordered by relevance', maxItems: 2 }),
    })
    .openapi({ description: 'Primary and alternative MCC assignments for the merchant' }),
);

export type MccSuggestion = z.infer<typeof MccSuggestionSchema>;
export type MerchantCategoryCodeSummary = z.infer<typeof MerchantCategoryCodeSummarySchema>;

export const MerchantDetailsSchema = reportSchemaRegistry.register(
  'MerchantDetails',
  z
    .object({
      email: z.string().nullish().openapi({ description: 'Business email address' }),
      emailAddresses: z.array(z.string()).nullish().openapi({
        description: 'Business email addresses (business-analysis column; preferred for display)',
      }),
      address: z.string().nullish().openapi({ description: 'Business address' }),
      physicalAddresses: z.array(z.string()).nullish().openapi({
        description: 'Physical addresses (business-analysis column; joined when address is absent)',
      }),
      country: z.string().nullish().openapi({ description: 'Country of operation' }),
      website: z.string().nullish().openapi({ description: 'Business website URL' }),
      industry: z.string().nullish().openapi({ description: 'Industry classification' }),
      companyName: z.string().nullish().openapi({ description: 'Company name' }),
      alternativeCompanyNames: z
        .array(z.string())
        .nullish()
        .openapi({ description: 'Alternative company names' }),
      countryCode: z.string().nullish().openapi({ description: 'ISO country code' }),
      foundedDate: z.string().nullish().openapi({ description: 'Date the company was founded' }),
      phoneNumber: z.string().nullish().openapi({ description: 'Business phone number' }),
      phoneNumbers: z.array(z.string()).nullish().openapi({
        description: 'Business phone numbers (business-analysis column; preferred for display)',
      }),
      registrationNumber: z
        .string()
        .nullish()
        .openapi({ description: 'Business registration number' }),
      associatedPersons: z
        .array(z.string())
        .nullish()
        .openapi({ description: 'Names of associated persons' }),
      detailsComparison: z
        .string()
        .nullish()
        .openapi({ description: 'Comparison of declared vs detected details' }),
    })
    .openapi({ description: 'Merchant business details extracted from analysis' }),
);

export const TagLevelEnum = z.enum(['positive', 'moderate', 'negative']);

export const TagSchema = reportSchemaRegistry.register(
  'Tag',
  z
    .object({
      name: z.string().openapi({ description: 'Tag name', example: 'Established Business' }),
      level: TagLevelEnum.openapi({ description: 'Tag severity level', example: 'positive' }),
    })
    .openapi({ description: 'Business assessment tag with severity level' }),
);

export const RiskTypeStatusSchema = reportSchemaRegistry.register(
  'RiskTypeStatus',
  z
    .object({
      status: z.enum(['alert', 'clear']).openapi({ description: 'Risk status', example: 'clear' }),
    })
    .openapi({ description: 'Status for a specific risk type' }),
);

export const RiskTypesSchema = reportSchemaRegistry.register(
  'RiskTypes',
  z
    .object({
      legal: RiskTypeStatusSchema.nullish().openapi({ description: 'Legal risk status' }),
      chargebacks: RiskTypeStatusSchema.nullish().openapi({
        description: 'Chargeback risk status',
      }),
      reputational: RiskTypeStatusSchema.nullish().openapi({
        description: 'Reputational risk status',
      }),
      contentFindings: RiskTypeStatusSchema.nullish().openapi({
        description: 'Content findings risk status',
      }),
      transactionLaundering: RiskTypeStatusSchema.nullish().openapi({
        description: 'Transaction laundering risk status',
      }),
    })
    .openapi({ description: 'Risk type assessments for the merchant' }),
);

// TODO: use df-schemas once admin is zod@v4 instead of duplicating schemas
/*
This is duplicated in libs/schemas/digital-footprint/src/aup.ts
because we can't use df-schemas with admin.

Please keep in sync when changing
 */
export const POLICY_FIT_STATUS_MAP = {
  NEUTRAL: 'neutral',
  RESTRICTED: 'restricted',
  PROHIBITED: 'prohibited',
  UNVERIFIED: 'unverified',
} as const;

export const POLICY_FIT_STATUSES = Object.values(POLICY_FIT_STATUS_MAP);

export const PolicyFitStatus = z.enum(['neutral', 'restricted', 'prohibited', 'unverified']);

export const PolicyFitSchema = reportSchemaRegistry.register(
  'PolicyFit',
  z
    .object({
      status: PolicyFitStatus.openapi({ description: 'Policy fit status', example: 'neutral' }),
      displayName: z.string().nullable().openapi({ description: 'Display name for the policy' }),
      version: z.coerce.number().nullable().openapi({ description: 'Policy version number' }),
      description: z.string().openapi({ description: 'Policy description' }),
      policyFitSnippet: z
        .string()
        .nullable()
        .openapi({ description: 'Policy fit explanation snippet' }),
      originalPolicyFileId: z
        .string()
        .nullish()
        .openapi({ description: 'S3 private bucket file Id' }),
    })
    .openapi({ description: 'Policy fit assessment result' }),
);

export type PolicyFit = z.infer<typeof PolicyFitSchema>;
export type PolicyFitStatus = z.infer<typeof PolicyFitStatus>;

const ServerLocationSchema = reportSchemaRegistry.register(
  'ServerLocation',
  z
    .object({
      ip: z.string().nullish().openapi({ description: 'Server IP address' }),
      country: z.string().nullish().openapi({ description: 'Server country' }),
      region: z.string().nullish().openapi({ description: 'Server region' }),
      city: z.string().nullish().openapi({ description: 'Server city' }),
      lat: z.number().nullish().openapi({ description: 'Latitude coordinate' }),
      lng: z.number().nullish().openapi({ description: 'Longitude coordinate' }),
      postalCode: z.string().nullish().openapi({ description: 'Postal code' }),
      timezone: z.string().nullish().openapi({ description: 'Server timezone' }),
      geonameId: z.number().nullish().openapi({ description: 'GeoNames ID' }),
    })
    .openapi({ description: 'Server geographic location information' }),
);

export const DomainInfoSchema = reportSchemaRegistry.register(
  'DomainInfo',
  z
    .object({
      createdAt: z
        .string()
        .nullish()
        .openapi({ description: 'Domain registration date', example: '2016-09-14T00:00:00.000Z' }),
      expiresAt: z
        .string()
        .nullish()
        .openapi({ description: 'Domain expiration date', example: '2026-09-14T00:00:00.000Z' }),
      updatedAt: z.string().nullish().openapi({ description: 'Domain last updated date' }),
      hostedCount: z
        .number()
        .nullish()
        .openapi({ description: 'Number of websites hosted on this domain' }),
      serverLocation: ServerLocationSchema.nullish(),
    })
    .openapi({ description: 'Domain registration and hosting information' }),
);

export const RiskIndicatorSchema = reportSchemaRegistry.register(
  'RiskIndicator',
  z
    .object({
      id: z.string().openapi({
        description: 'Unique identifier of the risk indicator',
        example: 'content-landing-page-without-active-content',
      }),
      name: z.string().nullish().openapi({
        description: 'Display name of the risk indicator',
        example: 'Landing Page Without Active Content',
      }),
      sourceUrl: z.string().nullish().openapi({
        description: 'URL where the risk was detected',
        example: 'https://www.example.com/',
      }),
      screenshot: z
        .object({
          screenshotUrl: z
            .string()
            .url()
            .nullish()
            .openapi({ description: 'URL to screenshot evidence' }),
        })
        .nullish()
        .openapi({ description: 'Screenshot evidence of the risk' }),
      explanation: z.string().nullish().openapi({
        description: 'Detailed explanation of risk implications',
        example:
          'The website lacks active content or functionality, suggesting it may be under development...',
      }),
      reason: z.string().nullish().openapi({
        description: 'Detailed reason why this risk was flagged',
        example: 'The website displays only placeholder content with a simple header...',
      }),
      quoteFromSource: z.string().nullish().openapi({
        description: 'Quote extracted from the source',
        example: 'This domain is for use in illustrative examples in documents.',
      }),
      riskLevel: z
        .enum(RISK_INDICATOR_RISK_LEVELS)
        .nullish()
        .openapi({
          description: 'Severity level of the risk',
          enum: ['low', 'moderate', 'high', 'critical'],
          example: 'critical',
        }),
      pageContext: z.string().nullish().optional().openapi({
        description: 'Page context where risk was found',
        example: 'About Us',
      }),
      status: z.enum(['detected', 'missing', 'unverified']).nullish().optional().openapi({
        description: 'Status of the risk indicator',
      }),
      cardSchemeContentRisk: z.boolean().nullish().openapi({
        description: 'Whether this content finding is flagged as a card-scheme content risk',
      }),
    })
    .passthrough()
    .openapi({ description: 'Risk indicator with detection details and evidence' }),
);

export const NewEcosystemRecordSchema = z.object({
  domain: z.string().openapi({ description: 'Related domain' }),
  relationship: z.string().openapi({ description: 'Relationship description' }),
  nodes: z.array(
    z.object({
      type: z.string().openapi({ description: 'Node type (e.g., Company Name, Phone Number)' }),
      value: z.string().openapi({ description: 'Node value' }),
    }),
  ),
});

export const LegacyEcosystemRecordSchema = z.object({
  domain: z.string().openapi({ description: 'Related domain' }),
  relatedNode: z.string().openapi({ description: 'Related node value' }),
  relatedNodeType: z.string().openapi({ description: 'Related node type' }),
});

export const EcosystemRecordSchema = reportSchemaRegistry.register(
  'EcosystemRecord',
  z.union([NewEcosystemRecordSchema, LegacyEcosystemRecordSchema]).openapi({
    description: 'Ecosystem relationship record linking related businesses and entities',
  }),
);

export const HighRiskVerticalAnalysisSectionSchema = z
  .object({
    displayName: z.string().openapi({ description: 'Section display name' }),
    status: z.enum(['clear', 'alert']).openapi({ description: 'Section status' }),
    content: z.string().openapi({ description: 'Section content analysis' }),
  })
  .strict();

export const HighRiskVerticalAnalysisResultSchema = reportSchemaRegistry.register(
  'HighRiskVerticalAnalysisResult',
  z
    .object({
      vertical: z.string().openapi({ description: 'High-risk vertical identifier' }),
      promptName: z.string().openapi({ description: 'Analysis prompt name' }),
      verticalDisplayName: z
        .string()
        .optional()
        .openapi({ description: 'Display name for the vertical' }),
      summary: z.string().optional().openapi({ description: 'Analysis summary' }),
      sections: z
        .array(HighRiskVerticalAnalysisSectionSchema)
        .optional()
        .openapi({ description: 'Analysis sections' }),
    })
    .strict()
    .openapi({ description: 'High-risk vertical analysis result' }),
);

export const OngoingMonitoringChangeSchema = reportSchemaRegistry.register(
  'OngoingMonitoringChange',
  z
    .object({
      category: z.string().openapi({
        description: 'Category of the change detected',
        example: 'risk_level',
      }),
      reasoning: z.string().openapi({
        description: 'Detailed reasoning for why this change is significant',
        example: 'Risk level increased from low to high',
      }),
      severity: z.enum(['low', 'medium', 'high']).openapi({
        description: 'Severity level of the change',
        example: 'high',
      }),
    })
    .openapi({ description: 'Change detected during ongoing monitoring' }),
);

export type OngoingMonitoringChange = z.infer<typeof OngoingMonitoringChangeSchema>;
export type OngoingMonitoringChanges = OngoingMonitoringChange[];

export const PreviousReportsSchema = reportSchemaRegistry.register(
  'PreviousReports',
  z
    .object({
      previousMonitoringReportId: z
        .string()
        .nullish()
        .openapi({ description: 'ID of the previous monitoring report' }),
      onboardingReportId: z
        .string()
        .nullish()
        .openapi({ description: 'ID of the original onboarding report' }),
    })
    .openapi({ description: 'References to previous reports for comparison' }),
);

export type PreviousReports = z.infer<typeof PreviousReportsSchema>;

// TODO: Deduplicate with libs/schemas/digital-footprint/src/company-registry-check.ts
// These schemas are duplicated here because the frontend cannot use @ballerine/digital-footprint-schemas
// due to Zod v3 vs v4 compatibility issues. Once frontend is migrated to Zod v4, remove this duplication
// and import from @ballerine/digital-footprint-schemas instead.
/**
 * Schema for company registry data
 * Represents structured data returned from company registry vendors (OpenCorporates, Kyckr, etc.)
 *
 * NOTE: This is a copy of CompanyRegistryDataSchema from @ballerine/digital-footprint-schemas
 */
export const CompanyRegistryDataSchema = reportSchemaRegistry.register(
  'CompanyRegistryData',
  z
    .object({
      name: z.string().openapi({ description: 'Registered company name' }),
      companyNumber: z.string().openapi({ description: 'Company registration number' }),
      jurisdictionCode: z.string().openapi({ description: 'Jurisdiction code (e.g., us_de, gb)' }),
      incorporationDate: z.string().nullable().openapi({ description: 'Date of incorporation' }),
      dissolutionDate: z
        .string()
        .nullable()
        .openapi({ description: 'Date of dissolution if applicable' }),
      companyType: z
        .string()
        .nullable()
        .openapi({ description: 'Type of company (e.g., LLC, Corporation)' }),
      registryUrl: z.string().nullable().openapi({ description: 'URL to official registry entry' }),
      openCorporatesUrl: z
        .string()
        .nullable()
        .optional()
        .openapi({ description: 'OpenCorporates profile URL' }),
      isBranch: z.boolean().openapi({ description: 'Whether this is a branch office' }),
      branchStatus: z.string().nullable().openapi({ description: 'Status of the branch' }),
      isInactive: z.boolean().openapi({ description: 'Whether the company is inactive' }),
      currentStatus: z
        .string()
        .nullable()
        .openapi({ description: 'Current company status', example: 'Active' }),
      source: z.object({
        publisher: z.string().openapi({ description: 'Data source publisher' }),
        url: z.string().openapi({ description: 'Source URL' }),
        retrievedAt: z.string().openapi({ description: 'Data retrieval timestamp' }),
      }),
      agentName: z.string().nullable().openapi({ description: 'Registered agent name' }),
      alternativeNames: z.array(
        z.object({
          companyName: z.string().openapi({ description: 'Alternative company name' }),
          type: z.string().nullable().openapi({ description: 'Name type' }),
          startDate: z.string().nullable().openapi({ description: 'Start date of name usage' }),
          endDate: z.string().nullable().openapi({ description: 'End date of name usage' }),
          language: z.string().nullable().openapi({ description: 'Language of the name' }),
        }),
      ),
      previousNames: z.array(
        z.object({
          companyName: z.string().openapi({ description: 'Previous company name' }),
          type: z.string().nullable().openapi({ description: 'Name type' }),
          startDate: z.string().nullable().openapi({ description: 'Start date' }),
          endDate: z.string().nullable().openapi({ description: 'End date' }),
          language: z.string().nullable().openapi({ description: 'Language' }),
        }),
      ),
      numberOfEmployees: z.number().nullable().openapi({ description: 'Number of employees' }),
      registeredAddressInFull: z
        .string()
        .nullable()
        .openapi({ description: 'Full registered address as string' }),
      registeredAddress: z
        .object({
          country: z.string().openapi({ description: 'Country' }),
          locality: z.string().nullable().openapi({ description: 'City/locality' }),
          postalCode: z.string().nullable().openapi({ description: 'Postal code' }),
          region: z.string().nullable().openapi({ description: 'State/region' }),
          streetAddress: z.string().openapi({ description: 'Street address' }),
        })
        .nullable(),
      industries: z.array(
        z.object({
          code: z.string().openapi({ description: 'Industry code' }),
          description: z.string().nullable().openapi({ description: 'Industry description' }),
          codeSchemeId: z.string().openapi({ description: 'Code scheme ID' }),
          codeSchemeName: z
            .string()
            .openapi({ description: 'Code scheme name (e.g., SIC, NAICS)' }),
          uid: z.string().openapi({ description: 'Unique identifier' }),
        }),
      ),
      identifiers: z.array(
        z.object({
          uid: z.string().openapi({ description: 'Identifier UID' }),
          identifierSystemCode: z.string().openapi({ description: 'Identifier system code' }),
          identifierSystemName: z.string().openapi({ description: 'Identifier system name' }),
        }),
      ),
      officers: z.array(
        z.object({
          name: z.string().openapi({ description: 'Officer name' }),
          position: z.string().nullable().openapi({ description: 'Position/title' }),
          startDate: z.string().nullable().openapi({ description: 'Start date in position' }),
          endDate: z.string().nullable().openapi({ description: 'End date in position' }),
          occupation: z.string().nullable().openapi({ description: 'Occupation' }),
          isInactive: z.boolean().openapi({ description: 'Whether the officer is inactive' }),
          currentStatus: z.string().nullable().openapi({ description: 'Current status' }),
          address: z.string().nullable().openapi({ description: 'Officer address' }),
        }),
      ),
    })
    .openapi({ description: 'Structured company registry data from official sources' }),
);

/**
 * Union schema for company registry check results
 * Success is determined by the boolean 'success' field
 *
 * NOTE: This is a copy of CompanyRegistryCheckResultSchema from @ballerine/digital-footprint-schemas
 */
export const CompanyRegistryCheckResultSchema = reportSchemaRegistry.register(
  'CompanyRegistryCheckResult',
  z
    .object({
      success: z.boolean().openapi({ description: 'Whether the registry check was successful' }),
      data: CompanyRegistryDataSchema.optional().openapi({
        description: 'Registry data if successful',
      }),
      error: z.string().optional().openapi({ description: 'Error message if unsuccessful' }),
      source: z.string().openapi({ description: 'Data source (e.g., opencorporates, kyckr)' }),
      jurisdictionCode: z.string().openapi({ description: 'Jurisdiction code used for lookup' }),
      retrievedAt: z.string().openapi({ description: 'Timestamp when data was retrieved' }),
    })
    .openapi({ description: 'Result of company registry check operation' }),
);

export type CompanyRegistryData = z.infer<typeof CompanyRegistryDataSchema>;
export type CompanyRegistryCheckResult = z.infer<typeof CompanyRegistryCheckResultSchema>;

/**
 * Stakeholder schema for all stakeholder roles (UBO, Director, Senior Officer, Signatory, Legal Representative, Other)
 */
export const StakeholderSchema = reportSchemaRegistry.register(
  'Stakeholder',
  z
    .object({
      firstName: z.string().nullish().openapi({ description: 'First name' }),
      middleName: z.string().nullish().openapi({ description: 'Middle name' }),
      lastName: z.string().nullish().openapi({ description: 'Last name' }),
      name: z.string().nullish().openapi({ description: 'Full name' }),
      title: z.string().nullish().openapi({ description: 'Title (e.g., Mr., Ms.)' }),
      role: z.enum(STAKEHOLDER_ROLES).nullish().openapi({ description: 'Stakeholder role' }),
      nationality: z.string().nullish().openapi({ description: 'Nationality' }),
      dateOfBirth: z.string().nullish().openapi({ description: 'Date of birth' }),
      cityOfBirth: z.string().nullish().openapi({ description: 'City of birth' }),
      countryOfBirth: z.string().nullish().openapi({ description: 'Country of birth' }),
      residency: z
        .object({
          country: z.string().nullish().openapi({ description: 'Residency country' }),
          state: z.string().nullish().openapi({ description: 'Residency state' }),
          address: z.string().nullish().openapi({ description: 'Residency address' }),
        })
        .nullish(),
    })
    .openapi({ description: 'Business stakeholder information (UBO, Director, etc.)' }),
);

export type Stakeholder = z.infer<typeof StakeholderSchema>;

/**
 * Transaction analysis input schema
 */
export const TransactionAnalysisSchema = z
  .object({
    enabled: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether transaction analysis is enabled' }),
    fileUrl: z.string().nullish().openapi({ description: 'URL to transaction data file' }),
  })
  .nullish();

export type TransactionAnalysis = z.infer<typeof TransactionAnalysisSchema>;

export const DeclaredDetailsSchema = reportSchemaRegistry.register(
  'DeclaredDetails',
  z
    .object({
      address: z.string().nullish().openapi({ description: 'Declared business address' }),
      mcc: z.string().nullish().openapi({ description: 'Declared MCC code' }),
      dba: z.array(z.string()).nullish().openapi({ description: 'Doing Business As names' }),
      companyName: z.string().nullish().openapi({ description: 'Company name' }),
      parentCompanyName: z.string().nullish().openapi({ description: 'Parent company name' }),
      additionalUrls: z
        .array(z.string())
        .nullish()
        .openapi({ description: 'Additional website URLs' }),
      addresses: z.array(z.string()).nullish().openapi({ description: 'Additional addresses' }),
      emails: z.array(z.string()).nullish().openapi({ description: 'Contact email addresses' }),
      phoneNumbers: z.array(z.string()).nullish().openapi({ description: 'Contact phone numbers' }),
      registrationNumber: z
        .string()
        .nullish()
        .openapi({ description: 'Business registration number' }),
      billingDescriptor: z.string().nullish().openapi({ description: 'Billing descriptor' }),
      icaBin: z.string().nullish().openapi({ description: 'ICA/BIN number' }),
      icaBinName: z.string().nullish().openapi({ description: 'ICA/BIN name' }),
      additionalInformation: z
        .string()
        .nullish()
        .openapi({ description: 'Additional information provided' }),
      stakeholders: z
        .array(StakeholderSchema)
        .nullish()
        .openapi({ description: 'Business stakeholders' }),
      companyRegistryCheck: z
        .object({
          enabled: z
            .boolean()
            .nullish()
            .openapi({ description: 'Whether company registry check is enabled' }),
          registrationNumber: z
            .string()
            .nullish()
            .openapi({ description: 'Registration number for lookup' }),
          country: z.string().nullish().openapi({ description: 'Country for registry lookup' }),
          state: z.string().nullish().openapi({ description: 'State for registry lookup' }),
        })
        .nullish(),
      transactionAnalysis: TransactionAnalysisSchema,
      msaFileAnalysis: z
        .object({
          persistedFileId: z.string().nullish().openapi({ description: 'Persisted MSA file ID' }),
          msaFileUrl: z.string().url().nullish().openapi({ description: 'MSA file URL' }),
        })
        .nullish(),
    })
    .nullish()
    .openapi({ description: 'Details declared by the merchant for verification' }),
);

export type DeclaredDetails = z.infer<typeof DeclaredDetailsSchema>;

const CredentialsSchema = z.object({
  username: z.string().min(1).openapi({ description: 'Username for gated content access' }),
  password: z.string().min(1).openapi({ description: 'Password for gated content access' }),
});

export const GenerateReportInputSchema = z.object({
  websiteUrl: z
    .string()
    .url()
    .openapi({ description: 'Website URL to analyze', example: 'https://www.example.com' }),
  companyName: z.string().optional().openapi({ description: 'Company name' }),
  merchantId: z.string().optional().openapi({ description: 'Merchant identifier' }),
  countryCode: z.string().max(2).optional().openapi({ description: 'ISO 2-letter country code' }),
  businessId: z.string().optional().openapi({ description: 'Business entity ID' }),
  businessCorrelationId: z
    .string()
    .optional()
    .openapi({ description: 'Client correlation ID for tracking' }),
  isOngoingMonitoringEnabled: z
    .boolean()
    .openapi({ description: 'Enable ongoing monitoring for this merchant' }),
  reportType: z
    .enum([MERCHANT_REPORT_TYPES[0], ...MERCHANT_REPORT_TYPES.slice(1)])
    .openapi({ description: 'Type of report to generate' }),
  declaredDetails: DeclaredDetailsSchema.optional(),
  credentials: CredentialsSchema.optional().openapi({
    description: 'Credentials for gated content access',
  }),
  msaFile: z.custom<File | undefined>(val => val === undefined || val instanceof File).optional(),
  transactionAnalysisFile: z
    .custom<File | undefined>(val => val === undefined || val instanceof File)
    .optional(),
});

export type GenerateReportInput = z.infer<typeof GenerateReportInputSchema>;

const GatedContentCheckSchema = reportSchemaRegistry.register(
  'GatedContentCheck',
  z
    .object({
      status: z
        .string()
        .openapi({ description: 'Gated content check status', example: 'completed' }),
      message: z.string().nullable().openapi({ description: 'User-friendly status message' }),
      errorCause: z.string().nullable().optional().openapi({
        description: 'Technical error cause code for analytics (e.g., login_loop, popup_loop)',
        example: 'login_loop',
      }),
      sessionUrl: z
        .string()
        .url()
        .nullable()
        .openapi({ description: 'Browser session recording URL' }),
      screenshotUrl: z
        .string()
        .url()
        .nullable()
        .optional()
        .openapi({ description: 'Screenshot of gated content (presigned URL)' }),
      screenshotFileId: z
        .string()
        .nullable()
        .optional()
        .openapi({ description: 'Screenshot file ID for storage proxy' }),
      videoUrl: z
        .string()
        .url()
        .nullable()
        .optional()
        .openapi({ description: 'Video recording URL (presigned URL)' }),
      videoFileId: z
        .string()
        .nullable()
        .optional()
        .openapi({ description: 'Video file ID for storage proxy' }),
    })
    .openapi({ description: 'Result of gated/login-protected content check' }),
);

const VisitorsCountriesSchema = reportSchemaRegistry.register(
  'VisitorsCountries',
  z
    .object({
      data: z
        .record(z.string(), z.number())
        .nullish()
        .openapi({
          description: 'Country name to visitor percentage mapping',
          example: { 'United States': 0.45, 'United Kingdom': 0.15, Germany: 0.1 },
        }),
      startDate: z
        .string()
        .nullish()
        .openapi({ description: 'Data period start date', example: '2025-03' }),
      endDate: z
        .string()
        .nullish()
        .openapi({ description: 'Data period end date', example: '2025-06' }),
    })
    .openapi({ description: 'Visitor geographic distribution data' }),
);

export const ReportDataSchema = reportSchemaRegistry.register(
  'ReportData',
  z
    .object({
      lineOfBusiness: z
        .string()
        .nullish()
        .openapi({ description: 'Detected line of business', example: 'E-commerce' }),
      companyName: z
        .string()
        .nullish()
        .openapi({ description: 'Company name from analysis', example: 'Example Corporation' }),
      mcc: MerchantCategoryCodeSummarySchema.nullish(),
      bounceRate: z.string().nullish().openapi({
        description: 'Website bounce rate as decimal string',
        example: '0.6630983310786992',
      }),
      timeOnSite: z
        .string()
        .nullish()
        .openapi({ description: 'Average time on site in seconds', example: '80.20825448947242' }),
      pagesPerVisit: z
        .string()
        .nullish()
        .openapi({ description: 'Average pages per visit', example: '1.9515820180580246' }),
      trafficSources: z
        .record(z.string(), z.number())
        .nullish()
        .openapi({
          description: 'Traffic source breakdown',
          example: { direct: 0.67, 'search / organic': 0.2, referral: 0.05 },
        }),
      visitorsCountries: VisitorsCountriesSchema.nullish(),
      monthlyVisits: z
        .record(z.string(), z.number())
        .nullish()
        .openapi({
          description: 'Monthly visit statistics',
          example: { '2025-01-01': 4222677, '2025-02-01': 3063373 },
        }),
      facebookPage: FacebookPageSchema.nullish(),
      instagramPage: InstagramPageSchema.nullish(),
      linkedinPage: SocialPage.nullish(),
      tiktokPage: SocialPage.nullish(),
      twitterPage: SocialPage.nullish(),
      merchantDetails: MerchantDetailsSchema.nullish(),
      tags: z.array(TagSchema).nullish().openapi({ description: 'Business assessment tags' }),
      unstructuredTags: z
        .array(TagSchema)
        .nullish()
        .openapi({ description: 'LLM-emitted business assessment tags (raw, unvalidated)' }),
      references: z
        .string()
        .nullish()
        .openapi({ description: 'Reference sources used in analysis' }),
      riskTypes: RiskTypesSchema.nullish(),
      policyFit: PolicyFitSchema.nullish(),
      domainInfo: DomainInfoSchema.nullish(),
      trafficRiskIndicators: z
        .array(RiskIndicatorSchema)
        .nullish()
        .openapi({ description: 'Traffic-related risk indicators' }),
      companyReputationRiskIndicators: z
        .array(RiskIndicatorSchema)
        .nullish()
        .openapi({ description: 'Company reputation risk indicators' }),
      websiteReputationRiskIndicators: z
        .array(RiskIndicatorSchema)
        .nullish()
        .openapi({ description: 'Website reputation risk indicators' }),
      contentRiskIndicators: z
        .array(RiskIndicatorSchema)
        .nullish()
        .openapi({ description: 'Content-related risk indicators' }),
      pricingRiskIndicators: z
        .array(RiskIndicatorSchema)
        .nullish()
        .openapi({ description: 'Pricing-related risk indicators' }),
      websiteStructureRiskIndicators: z
        .array(RiskIndicatorSchema)
        .nullish()
        .openapi({ description: 'Website structure risk indicators' }),
      homePageScreenshotUrl: z
        .string()
        .url()
        .nullish()
        .openapi({ description: 'URL to homepage screenshot' }),
      mediaScreenshots: z
        .array(
          z.object({
            url: z.string().url(),
            label: z.string().nullish(),
          }),
        )
        .nullish()
        .openapi({
          description: 'Additional screenshots from media analysis (pages beyond homepage)',
        }),
      ecosystem: z
        .array(EcosystemRecordSchema)
        .nullish()
        .openapi({ description: 'Related ecosystem analysis' }),
      isAlert: z
        .boolean()
        .nullish()
        .openapi({ description: 'Whether this report triggered an alert', example: false }),
      summary: z.string().nullish().openapi({
        description: 'Comprehensive analysis summary',
        example: 'The website at https://www.example.com/ is assessed at a Critical Risk level...',
      }),
      ongoingMonitoringSummary: z
        .string()
        .nullish()
        .openapi({ description: 'Summary for ongoing monitoring reports' }),
      ongoingMonitoringChanges: z
        .array(OngoingMonitoringChangeSchema)
        .nullish()
        .openapi({ description: 'Changes detected during monitoring' }),
      ongoingMonitoringChangesSnippet: z
        .string()
        .nullish()
        .openapi({ description: 'Brief summary of monitoring changes' }),
      companyRegistryData: CompanyRegistryCheckResultSchema.nullish(),
      riskScore: z.coerce
        .number()
        .nullish()
        .openapi({ description: 'Overall risk score (0-100)', example: 85 }),
      riskLevel: z.enum(MERCHANT_REPORT_RISK_LEVELS).nullish().openapi({
        description: 'Overall risk level assessment',
        example: 'critical',
      }),
      isWebsiteOffline: z.boolean().nullish().openapi({
        description: 'Whether the website was offline during analysis',
        example: false,
      }),
      allViolations: z
        .array(RiskIndicatorSchema.pick({ id: true, name: true, riskLevel: true, status: true }))
        .nullish()
        .openapi({ description: 'Summary of all violations detected' }),
      highRiskVerticalAnalysisResults: z
        .array(HighRiskVerticalAnalysisResultSchema)
        .nullish()
        .openapi({ description: 'High-risk vertical analysis results' }),
      gatedContentCheck: GatedContentCheckSchema.nullable().optional(),
    })
    .openapi({
      description:
        'Comprehensive report analysis data including risk indicators, traffic metrics, and business information',
    }),
);

export const ReportSchema = z
  .object({
    id: z.string().openapi({ description: 'Report unique identifier' }),
    reportType: z
      .enum([MERCHANT_REPORT_TYPES[0], ...MERCHANT_REPORT_TYPES.slice(1)])
      .openapi({ description: 'Type of report' }),
    createdAt: z
      .string()
      .datetime()
      .transform(value => new Date(value))
      .openapi({ description: 'Report creation timestamp' }),
    updatedAt: z
      .string()
      .datetime()
      .transform(value => new Date(value))
      .openapi({ description: 'Report last update timestamp' }),
    displayDate: z
      .string()
      .datetime()
      .transform(value => new Date(value))
      .openapi({ description: 'Display date for the report' }),
    publishedAt: z
      .string()
      .datetime()
      .nullish()
      .transform(value => (value ? new Date(value) : null))
      .openapi({ description: 'Report publication timestamp' }),
    status: z
      .enum([MERCHANT_REPORT_STATUSES[0], ...MERCHANT_REPORT_STATUSES.slice(1)])
      .openapi({ description: 'Current report status' }),
    state: z.enum(REPORT_WORKFLOW_STATES).nullish().openapi({ description: 'Report state' }),
    monitoringStatus: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether ongoing monitoring is active' }),
    website: z.object({
      id: z.string().nullable().openapi({
        description:
          'Website unique identifier (null when business_analysis has no websiteId or LEFT JOIN misses websites row)',
      }),
      url: z.string().nullable().openapi({
        description:
          'Website URL (null when business_analysis has no websiteId or LEFT JOIN misses websites row)',
      }),
      unsubscribedMonitoringAt: z
        .string()
        .datetime()
        .nullish()
        .openapi({ description: 'Monitoring unsubscription timestamp' }),
      monitoringScheduleType: z
        .string()
        .nullish()
        .openapi({ description: 'Monitoring schedule type (monthly, twice_monthly, etc.)' }),
      nextMonitoringRunAt: z
        .string()
        .datetime()
        .nullish()
        .openapi({ description: 'Next scheduled monitoring run date' }),
    }),
    customer: z.object({
      id: z.string().openapi({ description: 'Customer ID' }),
      displayName: z.string().openapi({ description: 'Customer display name' }),
      ongoingMonitoringEnabled: z
        .boolean()
        .openapi({ description: 'Customer-level monitoring setting' }),
    }),
    business: z.object({
      id: z.string().openapi({ description: 'Business entity ID' }),
      correlationId: z.string().nullish().openapi({ description: 'Client correlation ID' }),
    }),
    metadata: z
      .record(z.string(), z.unknown())
      .nullish()
      .openapi({ description: 'Additional metadata' }),
    companyName: z.string().nullish().openapi({ description: 'Company name' }),
    riskLevel: z
      .enum(MERCHANT_REPORT_RISK_LEVELS)
      .nullish()
      .openapi({ description: 'Current risk level' }),
    previousRiskLevel: z
      .enum(MERCHANT_REPORT_RISK_LEVELS)
      .nullish()
      .openapi({ description: 'Previous risk level for comparison' }),
    isAlert: z.boolean().nullish().openapi({ description: 'Whether this is an alert' }),
    isGatedContent: z.boolean().nullish().openapi({
      description: 'Whether the report has gated content and stored website credentials',
    }),
    previousReports: PreviousReportsSchema.nullish(),
    declaredDetails: DeclaredDetailsSchema,
    data: ReportDataSchema.passthrough()
      .nullable()
      .default({})
      .openapi({ description: 'Complete report analysis data' }),
  })
  .passthrough()
  .openapi({ description: 'Complete business report with all analysis data' });

// ============================================================================
// TLD Report API Response Schema
// This represents the raw structure returned by the TLD reports API endpoint
// ============================================================================

const TldReportWebsiteSchema = z.object({
  id: z.string(),
  url: z.string(),
  unsubscribedMonitoringAt: z.union([z.string(), z.date()]).nullable(),
});

const TldReportCustomerSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  ongoingMonitoringEnabled: z.boolean().optional(),
});

export const TldReportSchema = z.object({
  id: z.string(),
  reportType: z.string(),
  createdAt: z.union([z.string(), z.date()]),
  publishedAt: z.union([z.string(), z.date()]).nullable(),
  status: z.string(),
  riskLevel: z.string().nullable(),
  previousRiskLevel: z.string().nullable().optional(),
  isAlert: z.boolean().nullable(),
  website: TldReportWebsiteSchema,
  customer: TldReportCustomerSchema,
});

export type TldReport = z.infer<typeof TldReportSchema>;

export const TldReportsResponseSchema = z.object({
  data: z.array(TldReportSchema),
  totalItems: z.number(),
  totalPages: z.number().optional(),
});

export type TldReportsResponse = z.infer<typeof TldReportsResponseSchema>;
